import { FC, useCallback, useContext, useEffect, useState } from 'react';
import apiUrl from '../../assets/apiUrl';
import AuthContext from '../Auth';
import Sidebar from '../Sidebar';
import Device from './Device';
import styles from './index.module.scss';
import uaParser from 'ua-parser-js';
import AlertContext from '../Alert';
import QrReader from './QrReader';
import DetectBrowserIcon from './DetectBrowserIcon';
import ConfirmDialog from '../ConfirmDialog';
import { useHistory } from 'react-router';
import { Grid } from '@material-ui/core';
import withAuthenticationRequired from '../Auth/withAuthenticationRequired';

const Devices: FC = () => {
    const { fetchWrapper, logout } = useContext(AuthContext);
    const { show } = useContext(AlertContext);
    const [devices, setDevices] = useState([]);
    const loadDevices = useCallback(() => {
        fetchWrapper(`${apiUrl}/devices`).then(async (res) => {
            const devices = await res.json();
            setDevices(
                devices.map((e: any) => {
                    const parsed = uaParser(e.user_agent);
                    return {
                        session_id: e.session_id,
                        title: `${parsed.browser.name} ${parsed.browser.version}, ${parsed.os.name}`,
                        body: `${parsed.engine.name} ${
                            parsed.engine.version
                        }, ${parsed.device.type || 'Unknown device type'}`,
                        browserName: parsed.browser.name,
                    };
                })
            );
        });
    }, [fetchWrapper]);
    useEffect(() => {
        loadDevices();
    }, [loadDevices]);
    const deleteDevice = async (session_id: string) => {
        try {
            const apiCall = await fetchWrapper(
                `${apiUrl}/device/${session_id}`,
                {
                    method: 'DELETE',
                }
            );
            if (!apiCall.ok) {
                throw apiCall.statusText;
            }
            setDevices(devices.filter((e: any) => e.session_id !== session_id));
        } catch (err) {
            show({
                title: 'Error while disconnecting device',
                body: 'err',
                type: 'error',
            });
        }
    };
    const openQrReader = () => {
        setQrScannerOpen(true);
    };
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const postQr = async (token: string) => {
        await fetchWrapper(`${apiUrl}/auth/qr`, {
            method: 'POST',
            body: new URLSearchParams({
                qr_token: token,
            }),
        });
        setQrScannerOpen(false);
        loadDevices();
    };
    const history = useHistory();
    const disconnectAll = async (devices: any) => {
        const promises = [];
        for (const device of devices) {
            promises.push(deleteDevice(device.session_id));
        }
        await Promise.all(promises);
        logout();
        history.push('/');
    };
    const [disconnectAllOpen, setDisconnectAllOpen] = useState(false);
    return (
        <div className={styles.Devices}>
            <Sidebar />
            <div className={styles['devices-container']}>
                <button className={styles.qrButton} onClick={openQrReader}>
                    <i className="fas fa-qrcode" />
                    Scan QR code
                </button>
                <button
                    className={styles.disconnectAllButton}
                    onClick={() => setDisconnectAllOpen(true)}
                >
                    <i className="fas fa-broom" />
                    Disconnect All devices
                </button>
                <ConfirmDialog
                    title={'Are you sure you wish to disconnect all devices?'}
                    text={''}
                    open={disconnectAllOpen}
                    cancelButtonText={'Cancel'}
                    confirmButtonText={'Confirm'}
                    handleClose={(val) => {
                        if (val) disconnectAll(devices);
                        setDisconnectAllOpen(false);
                    }}
                />
                {qrScannerOpen && (
                    <QrReader
                        onScan={(data) => {
                            postQr(data);
                            setQrScannerOpen(false);
                        }}
                        onError={() => setQrScannerOpen(false)}
                        onClose={() => setQrScannerOpen(false)}
                    />
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {devices.map((e: any) => (
                                <Device
                                    key={e.session_id}
                                    title={e.title}
                                    body={e.body}
                                    icon={DetectBrowserIcon(e.browserName)}
                                    onDelete={() => deleteDevice(e.session_id)}
                                />
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default withAuthenticationRequired(Devices);
